import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, BellRing, BookOpen, Copy, Eraser, Inbox, KeyRound, Link as LinkIcon, ListChecks, Mail, MessageCircle, StickyNote, Trash2, UserPlus } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TimeSelect } from "@/components/TimeSelect";
import { AssociateRecipients } from "@/components/AssociateRecipients";
import { ForumColorSettings } from "@/components/ForumColorSettings";
import { StartupSettings } from "@/components/StartupSettings";
import { api, errText, API } from "@/lib/api";
import { AccessControlCard } from "@/components/AccessControlCard";
import { ConnectClaudeCard } from "@/components/ConnectClaudeCard";
import { BillingCard } from "@/components/BillingCard";
import { OfflineSettings } from "@/components/OfflineSettings";
import { BackgroundPicture } from "@/components/BackgroundPicture";
import { EmailInSettings } from "@/components/EmailInSettings";
import { BrandingSettings } from "@/components/BrandingSettings";
import { DeliveryEmails } from "@/components/DeliveryEmails";
import { fmtTime } from "@/lib/eventMeta";
import { useAuth } from "@/context/AuthContext";

const LEAD_OPTIONS = [15, 30, 45, 60, 90, 120, 180, 240];
const MEETING_LEAD_OPTIONS = [5, 10, 15, 30, 60];
const EXPIRY_DAY_OPTIONS = [0, 1, 2, 3, 7];
const ADVANCE_LABELS = ["Same day", "1 day", "2 days", "3 days", "4 days", "5 days", "6 days", "7 days"];
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({
  value: `${String(h).padStart(2, "0")}:00`,
  label: fmtTime(`${String(h).padStart(2, "0")}:00`),
}));

const TEXTURE_OPTIONS = [
  { value: "none", label: "Clean" },
  { value: "paper", label: "Paper" },
  { value: "architecture", label: "Architecture" },
  { value: "grid", label: "Blueprint grid" },
];

const ROLE_OPTIONS = [
  { value: "read_only", label: "Read only" },
  { value: "limited_edit", label: "Limited edit" },
  { value: "clerk", label: "Clerk — day sheet only" },
];

export default function Settings() {
  const { user, isOwner, workspace, role, savePreferences } = useAuth();
  const prefs = user?.preferences || {};
  const [collaborators, setCollaborators] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("read_only");
  const [keys, setKeys] = useState([]);
  const [keyName, setKeyName] = useState("");
  const [newKey, setNewKey] = useState(null);
  const [reminders, setReminders] = useState(null);
  const [timezones, setTimezones] = useState([]);
  const [savingReminders, setSavingReminders] = useState(false);
  const [wa, setWa] = useState(null);
  const [waToken, setWaToken] = useState("");
  const [links, setLinks] = useState([]);
  const [demo, setDemo] = useState(null);
  const [strength, setStrength] = useState(Math.round((prefs.texture_opacity ?? 0.1) * 100));

  useEffect(() => {
    setStrength(Math.round((prefs.texture_opacity ?? 0.1) * 100));
  }, [prefs.texture_opacity]);

  const load = useCallback(async () => {
    if (role === "clerk") return;
    try {
      const reqs = [api.get("/collaborators")];
      if (isOwner) reqs.push(api.get("/api-keys"));
      const [c, k] = await Promise.all(reqs);
      setCollaborators(c.data);
      if (k) setKeys(k.data);
    } catch (e) {
      toast.error(errText(e));
    }
  }, [isOwner, workspace?.workspace_id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    Promise.all([api.get("/reminders/settings"), api.get("/reminders/timezones")])
      .then(([s, t]) => {
        setReminders(s.data);
        setTimezones(t.data.timezones);
      })
      .catch((e) => toast.error(errText(e)));
    api
      .get("/whatsapp/settings")
      .then(({ data }) => setWa(data))
      .catch(() => setWa({ enabled: false, account_sid: "", from_number: "", to_number: "", content_sid: "", has_token: false }));
  }, []);

  const loadLinks = useCallback(async () => {
    if (!isOwner) return;
    try {
      const { data } = await api.get("/clerk/links");
      setLinks(data);
    } catch {
      setLinks([]);
    }
    try {
      const { data } = await api.get("/demo/status");
      setDemo(data);
    } catch {
      setDemo(null);
    }
  }, [isOwner]);

  const clearDemo = async () => {
    try {
      const { data } = await api.delete("/demo");
      const total = Object.values(data.removed).reduce((a, b) => a + b, 0);
      toast.success(`Sample entries cleared (${total} records)`);
      setDemo({ has_demo: false, counts: {} });
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const saveWhatsApp = async (patch = {}) => {
    const next = { ...wa, ...patch };
    try {
      const { data } = await api.put("/whatsapp/settings", {
        enabled: next.enabled,
        account_sid: next.account_sid || "",
        auth_token: waToken || null,
        from_number: next.from_number || "",
        to_number: next.to_number || "",
        content_sid: next.content_sid || null,
      });
      setWa(data);
      setWaToken("");
      toast.success("WhatsApp settings saved");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const testWhatsApp = async () => {
    try {
      const { data } = await api.post("/whatsapp/test");
      toast.success(`WhatsApp message queued (${data.sid || "sent"})`);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const forgetWhatsApp = async () => {
    try {
      const { data } = await api.delete("/whatsapp/settings");
      setWa(data);
      setWaToken("");
      toast.success("Twilio credentials removed");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const createLink = async () => {
    try {
      await api.post("/clerk/links");
      toast.success("Clerk link created");
      loadLinks();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const revokeLink = async (id) => {
    try {
      await api.delete(`/clerk/links/${id}`);
      toast.success("Link revoked");
      loadLinks();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const saveReminders = async (patch = {}) => {
    setSavingReminders(true);
    try {
      const { data } = await api.put("/reminders/settings", { ...reminders, ...patch });
      setReminders(data);
      toast.success("Reminder settings saved");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSavingReminders(false);
    }
  };

  const toggleAdvance = (day) => {
    const current = reminders.advance_days || [];
    const next = current.includes(day) ? current.filter((d) => d !== day) : [...current, day].sort();
    setReminders({ ...reminders, advance_days: next.length ? next : [0] });
  };

  const sendBriefNow = async () => {
    try {
      const { data } = await api.post("/reminders/send-brief");
      toast.success(
        data.email ? `Brief emailed · ${data.listings} listing(s)` : "Brief created in-app",
      );
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const updatePref = (patch) =>
    savePreferences({ ...(prefs || {}), ...patch }).catch((e) => toast.error(errText(e)));

  const invite = async () => {
    if (!inviteEmail.trim()) return toast.error("Enter a colleague's email");
    try {
      await api.post("/collaborators", { email: inviteEmail.trim(), role: inviteRole });
      toast.success("Access shared");
      setInviteEmail("");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const changeRole = async (collab, role) => {
    try {
      await api.put(`/collaborators/${collab.collaborator_id}`, { email: collab.email, role });
      toast.success("Permission updated");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const revokeAccess = async (id) => {
    try {
      await api.delete(`/collaborators/${id}`);
      toast.success("Access revoked");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const createKey = async () => {
    if (!keyName.trim()) return toast.error("Name the key");
    try {
      const { data } = await api.post("/api-keys", { name: keyName.trim() });
      setNewKey(data.api_key);
      setKeyName("");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const revokeKey = async (id) => {
    try {
      await api.delete(`/api-keys/${id}`);
      toast.success("Key revoked");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <div className="space-y-10 max-w-5xl" data-testid="settings-page">
      <div>
        <p className="label-caps">Workspace</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold mt-2">Settings</h1>
      </div>

      <section className="surface p-6 lg:p-8" data-testid="appearance-section">
        <h2 className="text-base md:text-lg font-heading font-semibold">Appearance</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Backgrounds and textures apply behind the dashboard and calendar. Cards stay solid so text
          is readable in a hurry.
        </p>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-8">
          <div className="space-y-3">
            <Label className="label-caps">Theme</Label>
            <div className="flex gap-2">
              {["light", "dark"].map((t) => (
                <button
                  key={t}
                  data-testid={`theme-option-${t}`}
                  onClick={() => updatePref({ theme: t })}
                  className={`px-5 py-3 rounded-sm border text-base font-medium capitalize transition-colors duration-200 ${
                    (prefs.theme || "light") === t
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border hover:bg-accent"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <Label className="label-caps">Background texture</Label>
            <div className="flex flex-wrap gap-2">
              {TEXTURE_OPTIONS.map((t) => (
                <button
                  key={t.value}
                  data-testid={`texture-option-${t.value}`}
                  onClick={() => updatePref({ texture: t.value })}
                  disabled={!!prefs.background_path}
                  className={`px-4 py-3 rounded-sm border text-base font-medium transition-colors duration-200 disabled:opacity-40 ${
                    (prefs.texture || "paper") === t.value
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border hover:bg-accent"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {prefs.background_path ? (
              <p className="text-xs text-muted-foreground">
                Your own picture is in use — remove it below to go back to a texture.
              </p>
            ) : null}
          </div>
        </div>

        <div className="mt-8 max-w-md space-y-3">
          <Label className="label-caps">
            {prefs.background_path ? "Picture strength" : "Texture strength"} · {strength}%
          </Label>
          <Slider
            data-testid="texture-opacity-slider"
            value={[strength]}
            min={3}
            max={prefs.background_path ? 100 : 35}
            step={1}
            onValueChange={(v) => {
              setStrength(v[0]);
              document.documentElement.style.setProperty("--bg-strength", String(v[0] / 100));
            }}
            onValueCommit={(v) => updatePref({ texture_opacity: v[0] / 100 })}
          />
          <p className="text-xs text-muted-foreground">
            Drag to fade the {prefs.background_path ? "picture" : "texture"} in and out — the change
            shows as you drag and is saved when you let go.
          </p>
        </div>

        <div className="mt-8 border-t border-border pt-8">
          <BackgroundPicture />
        </div>
      </section>

      <section className="surface p-6 lg:p-8" data-testid="reminders-section">
        <h2 className="text-base md:text-lg font-heading font-semibold inline-flex items-center gap-2">
          <BellRing className="h-5 w-5" /> Hearing reminders
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          A morning brief plus an alert before every fixed-time listing. Include listings up to seven
          days ahead, and get a weekly or monthly overview by email.
        </p>

        <div className="mt-6 border-b border-border pb-6">
          <DeliveryEmails />
        </div>

        {reminders ? (
          <>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                ["email_enabled", "Email me", "Brief, alerts and overviews by email"],
                ["inapp_enabled", "In-app alerts", "Shown under the bell in the header"],
              ].map(([key, title, hint]) => (
                <div
                  key={key}
                  className="flex items-center justify-between border border-border rounded-sm px-4 py-3"
                >
                  <div>
                    <p className="text-base font-semibold">{title}</p>
                    <p className="text-sm text-muted-foreground">{hint}</p>
                  </div>
                  <Switch
                    data-testid={`reminder-${key}`}
                    checked={!!reminders[key]}
                    onCheckedChange={(v) => setReminders({ ...reminders, [key]: v })}
                  />
                </div>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="space-y-2">
                <Label className="label-caps">Morning brief at</Label>
                <Select
                  value={reminders.brief_time}
                  onValueChange={(v) => setReminders({ ...reminders, brief_time: v })}
                >
                  <SelectTrigger data-testid="reminder-brief-time" className="rounded-sm h-11 text-base">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {HOUR_OPTIONS.map((h) => (
                      <SelectItem key={h.value} value={h.value}>
                        {h.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Time zone</Label>
                <Select
                  value={reminders.timezone}
                  onValueChange={(v) => setReminders({ ...reminders, timezone: v })}
                >
                  <SelectTrigger data-testid="reminder-timezone" className="rounded-sm h-11 text-base">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timezones.map((tz) => (
                      <SelectItem key={tz} value={tz}>
                        {tz}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Alert before listing</Label>
                <Select
                  value={String(reminders.alert_lead_minutes)}
                  onValueChange={(v) => setReminders({ ...reminders, alert_lead_minutes: Number(v) })}
                >
                  <SelectTrigger data-testid="reminder-lead" className="rounded-sm h-11 text-base">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_OPTIONS.map((m) => (
                      <SelectItem key={m} value={String(m)}>
                        {m >= 60 ? `${m / 60} hour${m > 60 ? "s" : ""}` : `${m} minutes`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <Label className="label-caps">Brief covers listings</Label>
              <div className="flex flex-wrap gap-2">
                {ADVANCE_LABELS.map((label, day) => (
                  <button
                    key={day}
                    data-testid={`reminder-advance-${day}`}
                    onClick={() => toggleAdvance(day)}
                    className={`px-4 py-2.5 rounded-sm border text-sm font-medium transition-colors duration-200 ${
                      (reminders.advance_days || []).includes(day)
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border hover:bg-accent"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                ["weekly_overview", "Weekly overview", "Emailed every Monday morning"],
                ["monthly_overview", "Monthly overview", "Emailed on the 1st of each month"],
              ].map(([key, title, hint]) => (
                <div
                  key={key}
                  className="flex items-center justify-between border border-border rounded-sm px-4 py-3"
                >
                  <div>
                    <p className="text-base font-semibold">{title}</p>
                    <p className="text-sm text-muted-foreground">{hint}</p>
                  </div>
                  <Switch
                    data-testid={`reminder-${key}`}
                    checked={!!reminders[key]}
                    onCheckedChange={(v) => setReminders({ ...reminders, [key]: v })}
                  />
                </div>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between border border-border rounded-sm px-4 py-3">
                <div>
                  <p className="text-base font-semibold">End-of-day to-do check</p>
                  <p className="text-sm text-muted-foreground">
                    Asks whether to roll unfinished to-dos over to tomorrow
                  </p>
                </div>
                <Switch
                  data-testid="reminder-todo_rollover"
                  checked={!!reminders.todo_rollover}
                  onCheckedChange={(v) => setReminders({ ...reminders, todo_rollover: v })}
                />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Ask me at</Label>
                <Select
                  value={String(reminders.todo_hour ?? 20)}
                  onValueChange={(v) => setReminders({ ...reminders, todo_hour: Number(v) })}
                >
                  <SelectTrigger data-testid="reminder-todo-hour" className="rounded-sm h-11 text-base">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {Array.from({ length: 24 }, (_, h) => (
                      <SelectItem key={h} value={String(h)}>
                        {`${String(h).padStart(2, "0")}:00`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between border border-border rounded-sm px-4 py-3">
                <div>
                  <p className="text-base font-semibold">Meeting join reminder</p>
                  <p className="text-sm text-muted-foreground">
                    Pings you with the joining link before a video hearing
                  </p>
                </div>
                <Switch
                  data-testid="reminder-meeting_reminder"
                  checked={!!reminders.meeting_reminder}
                  onCheckedChange={(v) => setReminders({ ...reminders, meeting_reminder: v })}
                />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Ping me before the meeting</Label>
                <Select
                  value={String(reminders.meeting_lead_minutes ?? 10)}
                  onValueChange={(v) =>
                    setReminders({ ...reminders, meeting_lead_minutes: Number(v) })
                  }
                >
                  <SelectTrigger
                    data-testid="reminder-meeting-lead"
                    className="rounded-sm h-11 text-base"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MEETING_LEAD_OPTIONS.map((m) => (
                      <SelectItem key={m} value={String(m)}>{`${m} minutes before`}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  The checker runs every 15 minutes, so a shorter lead arrives at the nearest check
                  before the meeting.
                </p>
              </div>
              <div className="flex items-center justify-between border border-border rounded-sm px-4 py-3 md:col-span-2">
                <div>
                  <p className="text-base font-semibold">Evening uncategorized check</p>
                  <p className="text-sm text-muted-foreground">
                    Lists diary entries with no matter linked, at the same end-of-day hour
                  </p>
                </div>
                <Switch
                  data-testid="reminder-uncategorized_nudge"
                  checked={!!reminders.uncategorized_nudge}
                  onCheckedChange={(v) => setReminders({ ...reminders, uncategorized_nudge: v })}
                />
              </div>
              <div className="flex items-center justify-between border border-border rounded-sm px-4 py-3 md:col-span-2">
                <div>
                  <p className="text-base font-semibold">What falls due today</p>
                  <p className="text-sm text-muted-foreground">
                    A short morning list of to-dos due today and anything overdue, across all matters
                  </p>
                </div>
                <Switch
                  data-testid="reminder-due_today_digest"
                  checked={reminders.due_today_digest !== false}
                  onCheckedChange={(v) => setReminders({ ...reminders, due_today_digest: v })}
                />
              </div>
              <div className="flex items-center justify-between border border-border rounded-sm px-4 py-3 md:col-span-2">
                <div>
                  <p className="text-base font-semibold">Clash warning the evening before</p>
                  <p className="text-sm text-muted-foreground">
                    Two matters fixed at the same hour tomorrow, or one fixed on a travel / all-day
                    date
                  </p>
                </div>
                <Switch
                  data-testid="reminder-clash_nudge"
                  checked={reminders.clash_nudge !== false}
                  onCheckedChange={(v) => setReminders({ ...reminders, clash_nudge: v })}
                />
              </div>
              <div className="flex items-center justify-between border border-border rounded-sm px-4 py-3 md:col-span-2">
                <div>
                  <p className="text-base font-semibold">Dictated notes to read</p>
                  <p className="text-sm text-muted-foreground">
                    An evening list of voice hearing logs filed without review, with the bell notice
                  </p>
                </div>
                <Switch
                  data-testid="reminder-voice_review_nudge"
                  checked={reminders.voice_review_nudge !== false}
                  onCheckedChange={(v) => setReminders({ ...reminders, voice_review_nudge: v })}
                />
              </div>
              <div className="flex items-center justify-between border border-border rounded-sm px-4 py-3 md:col-span-2">
                <div>
                  <p className="text-base font-semibold">Post-its about to lapse</p>
                  <p className="text-sm text-muted-foreground">
                    A morning list of notes reaching their expiry date, sent with the brief
                  </p>
                </div>
                <Switch
                  data-testid="reminder-postit_expiry_nudge"
                  checked={!!reminders.postit_expiry_nudge}
                  onCheckedChange={(v) => setReminders({ ...reminders, postit_expiry_nudge: v })}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="label-caps">Warn me this far ahead</Label>
                <Select
                  value={String(reminders.postit_expiry_days ?? 3)}
                  onValueChange={(v) =>
                    setReminders({ ...reminders, postit_expiry_days: Number(v) })
                  }
                >
                  <SelectTrigger
                    data-testid="reminder-postit-expiry-days"
                    className="rounded-sm h-11 text-base"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPIRY_DAY_OPTIONS.map((d) => (
                      <SelectItem key={d} value={String(d)}>
                        {d === 0 ? "Only on the day itself" : `${d} day${d === 1 ? "" : "s"} ahead`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <Button
                data-testid="save-reminders-btn"
                onClick={() => saveReminders()}
                disabled={savingReminders}
                className="rounded-full h-12 px-6"
              >
                {savingReminders ? "Saving…" : "Save reminders"}
              </Button>
              <Button
                variant="outline"
                data-testid="send-brief-now-btn"
                onClick={sendBriefNow}
                className="rounded-full h-12 px-5"
              >
                <Mail className="h-5 w-5 mr-2" /> Send my brief now
              </Button>
              <Button
                variant="outline"
                data-testid="send-uncategorized-btn"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/reminders/send-uncategorized");
                    toast.success(
                      data.entries
                        ? `${data.entries} uncategorized entr${
                            data.entries === 1 ? "y" : "ies"
                          } sent`
                        : "Nothing uncategorized — nothing sent",
                    );
                  } catch (e) {
                    toast.error(errText(e));
                  }
                }}
                className="rounded-full h-12 px-5"
              >
                <Inbox className="h-5 w-5 mr-2" /> Send uncategorized list
              </Button>
              <Button
                variant="outline"
                data-testid="send-expiry-nudge-btn"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/reminders/send-expiry-nudge");
                    toast.success(
                      data.notes
                        ? `${data.notes} post-it${data.notes === 1 ? "" : "s"} about to lapse sent`
                        : "Nothing lapsing soon — nothing sent",
                    );
                  } catch (e) {
                    toast.error(errText(e));
                  }
                }}
                className="rounded-full h-12 px-5"
              >
                <StickyNote className="h-5 w-5 mr-2" /> Send lapsing post-its
              </Button>
              <Button
                variant="outline"
                data-testid="send-clash-nudge-btn"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/reminders/send-clash-nudge");
                    toast.success(
                      data.clashes
                        ? `${data.clashes} clash${data.clashes === 1 ? "" : "es"} tomorrow sent`
                        : "Nothing clashing tomorrow — nothing sent",
                    );
                  } catch (e) {
                    toast.error(errText(e));
                  }
                }}
                className="rounded-full h-12 px-5"
              >
                <AlertTriangle className="h-5 w-5 mr-2" /> Send tomorrow's clashes
              </Button>
              <Button
                variant="outline"
                data-testid="send-due-today-btn"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/reminders/send-due-today");
                    const bits = [
                      data.overdue ? `${data.overdue} overdue` : null,
                      data.due_today ? `${data.due_today} due today` : null,
                    ].filter(Boolean);
                    toast.success(
                      bits.length ? `Sent · ${bits.join(", ")}` : "Nothing falls due today",
                    );
                  } catch (e) {
                    toast.error(errText(e));
                  }
                }}
                className="rounded-full h-12 px-5"
              >
                <ListChecks className="h-5 w-5 mr-2" /> Send what falls due today
              </Button>
            </div>
          </>
        ) : (
          <div className="mt-6 h-24 border border-border rounded-sm animate-pulse" />
        )}
      </section>

      <StartupSettings />

      <ForumColorSettings />

      <AssociateRecipients />

      <section className="surface p-6 lg:p-8" data-testid="whatsapp-section">        <h2 className="text-base md:text-lg font-heading font-semibold inline-flex items-center gap-2">
          <MessageCircle className="h-5 w-5" /> WhatsApp alerts
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Optional. Add your own Twilio WhatsApp credentials and the same brief and listing alerts
          also arrive on your phone. Your auth token is encrypted and never shown again.
        </p>

        {wa ? (
          <>
            <div className="mt-6 flex items-center justify-between border border-border rounded-sm px-4 py-3 max-w-xl">
              <div>
                <p className="text-base font-semibold">Send alerts on WhatsApp</p>
                <p className="text-sm text-muted-foreground">
                  {wa.has_token ? "Twilio credentials saved" : "Credentials needed to enable"}
                </p>
              </div>
              <Switch
                data-testid="whatsapp-enabled-switch"
                checked={!!wa.enabled}
                onCheckedChange={(v) => setWa({ ...wa, enabled: v })}
              />
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl">
              <div className="space-y-2">
                <Label className="label-caps">Twilio Account SID</Label>
                <Input
                  data-testid="whatsapp-sid-input"
                  value={wa.account_sid || ""}
                  onChange={(e) => setWa({ ...wa, account_sid: e.target.value })}
                  placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  className="rounded-sm h-12 text-base"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Auth token</Label>
                <Input
                  data-testid="whatsapp-token-input"
                  type="password"
                  value={waToken}
                  onChange={(e) => setWaToken(e.target.value)}
                  placeholder={wa.has_token ? "•••••••• (saved)" : "Twilio auth token"}
                  className="rounded-sm h-12 text-base"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">WhatsApp sender</Label>
                <Input
                  data-testid="whatsapp-from-input"
                  value={wa.from_number || ""}
                  onChange={(e) => setWa({ ...wa, from_number: e.target.value })}
                  placeholder="+14155238886 (Twilio sandbox)"
                  className="rounded-sm h-12 text-base"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Your WhatsApp number</Label>
                <Input
                  data-testid="whatsapp-to-input"
                  value={wa.to_number || ""}
                  onChange={(e) => setWa({ ...wa, to_number: e.target.value })}
                  placeholder="+919876543210"
                  className="rounded-sm h-12 text-base"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="label-caps">Approved template SID (optional)</Label>
                <Input
                  data-testid="whatsapp-template-input"
                  value={wa.content_sid || ""}
                  onChange={(e) => setWa({ ...wa, content_sid: e.target.value })}
                  placeholder="HXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx — leave blank for sandbox"
                  className="rounded-sm h-12 text-base"
                />
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <Button
                data-testid="save-whatsapp-btn"
                onClick={() => saveWhatsApp()}
                className="rounded-full h-12 px-6"
              >
                Save WhatsApp settings
              </Button>
              <Button
                variant="outline"
                data-testid="test-whatsapp-btn"
                onClick={testWhatsApp}
                disabled={!wa.enabled}
                className="rounded-full h-12 px-5"
              >
                Send test message
              </Button>
              {wa.has_token ? (
                <Button
                  variant="outline"
                  data-testid="forget-whatsapp-btn"
                  onClick={forgetWhatsApp}
                  className="rounded-full h-12 px-5 text-destructive border-destructive/40 hover:bg-destructive/10"
                >
                  Forget credentials
                </Button>
              ) : null}
            </div>
          </>
        ) : (
          <div className="mt-6 h-24 border border-border rounded-sm animate-pulse" />
        )}
      </section>

      {isOwner ? <AccessControlCard /> : null}

      {isOwner ? <ConnectClaudeCard /> : null}

      {isOwner ? <BillingCard /> : null}

      <OfflineSettings prefs={prefs || {}} onSave={(next) => updatePref(next)} />

      <section className="surface p-6 lg:p-8" data-testid="sharing-section">
        <h2 className="text-base md:text-lg font-heading font-semibold">Share with colleagues</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Read-only can view everything. Limited edit can add or change listings, matters and hearing
          logs, but cannot manage courts, sharing or API keys.
        </p>

        {isOwner ? (
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Input
              data-testid="invite-email-input"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="colleague@chambers.com"
              className="rounded-sm h-12 text-base sm:max-w-sm"
            />
            <Select value={inviteRole} onValueChange={setInviteRole}>
              <SelectTrigger data-testid="invite-role-select" className="rounded-sm h-12 text-base sm:w-52">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button data-testid="invite-btn" onClick={invite} className="rounded-full h-12 px-6">
              <UserPlus className="h-5 w-5 mr-2" /> Share
            </Button>
          </div>
        ) : null}

        <div className="mt-6 divide-y divide-border">
          {collaborators.map((c) => (
            <div
              key={c.collaborator_id}
              data-testid={`collaborator-${c.collaborator_id}`}
              className="py-4 flex flex-wrap items-center gap-4 justify-between"
            >
              <div>
                <p className="text-base font-semibold">{c.name || c.email}</p>
                <p className="text-sm text-muted-foreground">{c.email}</p>
              </div>
              <div className="flex items-center gap-2">
                {isOwner ? (
                  <>
                    <Select value={c.role} onValueChange={(v) => changeRole(c, v)}>
                      <SelectTrigger
                        data-testid={`collab-role-${c.collaborator_id}`}
                        className="rounded-sm h-10 w-44 text-sm"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLE_OPTIONS.map((r) => (
                          <SelectItem key={r.value} value={r.value}>
                            {r.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <button
                      data-testid={`revoke-collab-${c.collaborator_id}`}
                      onClick={() => revokeAccess(c.collaborator_id)}
                      className="h-10 w-10 grid place-items-center rounded-sm text-destructive hover:bg-destructive/10 transition-colors duration-200"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <span className="chip chip-session">{c.role}</span>
                )}
              </div>
            </div>
          ))}
          {collaborators.length === 0 ? (
            <p className="py-4 text-sm text-muted-foreground">No one else has access yet.</p>
          ) : null}
        </div>
      </section>

      {isOwner && demo?.has_demo ? (
        <section className="surface p-6 lg:p-8" data-testid="demo-data-section">
          <h2 className="text-base md:text-lg font-heading font-semibold inline-flex items-center gap-2">
            <Eraser className="h-5 w-5" /> Sample entries
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            This diary was set up with worked examples so every feature had something to show. Clear
            them once your real matters are in — anything you added yourself stays untouched.
          </p>

          <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm">
            {[
              ["matters", "matters"],
              ["events", "listings"],
              ["hearing_logs", "hearing logs"],
              ["courts", "courts"],
              ["collaborators", "shared people"],
              ["attachments", "attachments"],
            ].map(([key, label]) =>
              demo.counts[key] ? (
                <span key={key} data-testid={`demo-count-${key}`}>
                  <strong className="text-foreground">{demo.counts[key]}</strong>{" "}
                  <span className="text-muted-foreground">{label}</span>
                </span>
              ) : null,
            )}
          </div>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                data-testid="clear-demo-btn"
                className="mt-6 rounded-full h-12 px-6 bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                <Eraser className="h-5 w-5 mr-2" /> Clear sample entries
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-sm" data-testid="clear-demo-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-heading text-2xl">
                  Clear the sample entries?
                </AlertDialogTitle>
                <AlertDialogDescription className="text-sm">
                  The example courts, matters, listings, hearing logs, attachments and shared
                  colleagues will be removed. Your own entries are kept — any of yours that pointed
                  at a sample matter becomes an unlinked placeholder. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-full h-11" data-testid="clear-demo-cancel">
                  Keep them
                </AlertDialogCancel>
                <AlertDialogAction
                  data-testid="clear-demo-confirm"
                  onClick={clearDemo}
                  className="rounded-full h-11 bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Clear sample entries
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </section>
      ) : null}

      {isOwner ? (
        <section className="surface p-6 lg:p-8" data-testid="clerk-links-section">
          <h2 className="text-base md:text-lg font-heading font-semibold inline-flex items-center gap-2">
            <LinkIcon className="h-5 w-5" /> Clerk handoff links
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            A read-only link showing today's and tomorrow's sheet plus upcoming out-of-station days —
            no sign-in needed, revoke any time. Invite a clerk by email above for a sign-in version.
          </p>

          <Button
            data-testid="create-clerk-link-btn"
            onClick={createLink}
            className="mt-6 rounded-full h-12 px-6"
          >
            Create clerk link
          </Button>

          <div className="mt-6 divide-y divide-border">
            {links.map((l) => {
              const url = `${window.location.origin}/clerk/${l.token}`;
              return (
                <div
                  key={l.link_id}
                  data-testid={`clerk-link-${l.link_id}`}
                  className="py-4 flex flex-wrap items-center gap-3 justify-between"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-mono break-all">{url}</p>
                    <p className="text-xs text-muted-foreground mt-1">{l.views || 0} views</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      data-testid={`copy-clerk-link-${l.link_id}`}
                      onClick={() => {
                        navigator.clipboard?.writeText(url);
                        toast.success("Link copied");
                      }}
                      className="h-10 w-10 grid place-items-center rounded-sm hover:bg-accent transition-colors duration-200"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    <button
                      data-testid={`revoke-clerk-link-${l.link_id}`}
                      onClick={() => revokeLink(l.link_id)}
                      className="h-10 w-10 grid place-items-center rounded-sm text-destructive hover:bg-destructive/10 transition-colors duration-200"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
            {links.length === 0 ? (
              <p className="py-4 text-sm text-muted-foreground">No clerk links yet.</p>
            ) : null}
          </div>
        </section>
      ) : null}

      {isOwner ? <BrandingSettings /> : null}

      {isOwner ? <EmailInSettings /> : null}

      {isOwner ? (
        <section className="surface p-6 lg:p-8" data-testid="api-section">
          <h2 className="text-base md:text-lg font-heading font-semibold">Ingestion API &amp; webhooks</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Push client data, matter titles and case numbers from your practice-management tools.
          </p>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Input
              data-testid="api-key-name-input"
              value={keyName}
              onChange={(e) => setKeyName(e.target.value)}
              placeholder="e.g. Practice manager sync"
              className="rounded-sm h-12 text-base sm:max-w-sm"
            />
            <Button data-testid="create-api-key-btn" onClick={createKey} className="rounded-full h-12 px-6">
              <KeyRound className="h-5 w-5 mr-2" /> Generate key
            </Button>
          </div>

          {newKey ? (
            <div
              data-testid="new-api-key-banner"
              className="mt-4 border border-border rounded-sm p-4 bg-secondary/70"
            >
              <p className="label-caps">Copy this now — it is shown once</p>
              <div className="mt-2 flex items-center gap-3">
                <code className="text-sm break-all">{newKey}</code>
                <button
                  data-testid="copy-api-key-btn"
                  onClick={() => {
                    navigator.clipboard?.writeText(newKey);
                    toast.success("Key copied");
                  }}
                  className="h-9 w-9 grid place-items-center rounded-sm hover:bg-accent transition-colors duration-200"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : null}

          <div className="mt-6 divide-y divide-border">
            {keys.map((k) => (
              <div
                key={k.key_id}
                data-testid={`api-key-${k.key_id}`}
                className="py-4 flex items-center justify-between gap-4"
              >
                <div>
                  <p className="text-base font-semibold">{k.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {k.key_prefix}… · {k.call_count || 0} calls
                  </p>
                </div>
                <button
                  data-testid={`revoke-key-${k.key_id}`}
                  onClick={() => revokeKey(k.key_id)}
                  className="h-10 w-10 grid place-items-center rounded-sm text-destructive hover:bg-destructive/10 transition-colors duration-200"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            {keys.length === 0 ? (
              <p className="py-4 text-sm text-muted-foreground">No API keys yet.</p>
            ) : null}
          </div>

          <div className="mt-6 border border-border rounded-sm p-4 overflow-x-auto">
            <p className="label-caps mb-2">Endpoint</p>
            <pre className="text-xs leading-relaxed whitespace-pre-wrap break-all">{`POST ${API}/ingest
Header: X-API-Key: <your key>

{
  "matters": [
    { "title": "Sharma v. State", "case_number": "CRL.A. 512/2026", "client_name": "R. Sharma" }
  ],
  "events": [
    { "date": "2026-07-14", "event_type": "fixed_time", "start_time": "12:00",
      "case_number": "CRL.A. 512/2026", "title": "Arguments" }
  ]
}

Webhook alias: POST ${API}/ingest/webhook`}</pre>
          </div>

          <div className="mt-6 border border-border rounded-sm p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="label-caps">Full API reference</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Every endpoint, both authentication methods and copy-paste curl &amp; Python recipes —
                the document to hand to any developer connecting another app.
              </p>
            </div>
            <Link to="/docs">
              <Button data-testid="open-api-docs-btn" variant="outline" className="rounded-full h-10 px-5">
                <BookOpen className="h-4 w-4 mr-2" /> Open the API reference
              </Button>
            </Link>
          </div>
        </section>
      ) : null}
    </div>
  );
}
